import { useAppDispatch } from 'app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditPlaceMutation, useGetPlaceWithDocumentQuery } from '../services';
import { useAddDocumentMutation, useDeleteDocumentMutation } from 'services/documents';
import { useGetTerminalsQuery } from 'features/terminal/services';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useForm, ValidationErrorsType, ValidationErrorType } from 'hooks/useForm';
import { PLACE_FIELDS_SCHEMA, PLACE_INITIAL_FIELDS } from '../constants/place-fields';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { isEmpty, trimValues } from 'utils/object-helpers';
import { dataURLtoFile } from 'utils/file-helpers';
import { DeliveryZonePlaceConfig, Place } from '../types/dtos';
import { useRole } from 'hooks/useRole';
import { useGetCitiesQuery } from 'features/city/service';
import { validateConfigFields } from 'utils/products-config-validation-helper';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from 'hooks/useWorkspace';

type AllErrorsType<T> = ValidationErrorsType<T> & {
    [key: string]: ValidationErrorType | undefined;
};

const useController = () => {
    const [deliveryZonePlaceConfig, setDeliveryZonePlaceConfig] = useState<DeliveryZonePlaceConfig[]>([]);
    const [priceErrors, setPriceErrors] = useState<Record<string, ValidationErrorType>>({});
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [editPlace, { isLoading: isEditPlaceLoading }] = useEditPlaceMutation();
    const [addDocument, { isLoading: isAddDocumentLoading }] = useAddDocumentMutation();
    const [deleteDocument, { isLoading: isDeleteDocumentLoading }] = useDeleteDocumentMutation();
    const { data: place } = useGetPlaceWithDocumentQuery(id as string);
    const { data: terminals, isLoading: isGetTerminalsLoading } = useGetTerminalsQuery(FETCH_ALL_PARAMS);
    const { data: cities, isLoading: isGetCitiesLoading } = useGetCitiesQuery(FETCH_ALL_PARAMS);

    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: PLACE_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });
    const { websiteId, websiteName } = useWorkspace();
    const { isAdmin } = useRole();
    const initialDeliveryPriceConfigsRef = useRef<DeliveryZonePlaceConfig[]>([]);
    const allErrors: AllErrorsType<typeof PLACE_INITIAL_FIELDS> = useMemo(() => {
        return {
            ...errors,
            ...priceErrors,
        };
    }, [errors, priceErrors]);

    useEffect(() => {
        if (place) {
            if (place.workingHours.length === 0) {
                setInitialFormData({
                    ...PLACE_INITIAL_FIELDS,
                    ...place,
                    workingHours: PLACE_INITIAL_FIELDS.workingHours,
                });
                let newZones;
                if (place.deliveryZonePlacePrices)
                    newZones = [...place.deliveryZonePlacePrices].sort(
                        (a, b) => Number(a.price ? a.price : '0') - Number(b.price ? b.price : '0'),
                    );
                setDeliveryZonePlaceConfig(newZones);

                if (initialDeliveryPriceConfigsRef.current.length === 0) {
                    initialDeliveryPriceConfigsRef.current = [...newZones];
                }
            } else {
                setInitialFormData({ ...PLACE_INITIAL_FIELDS, ...place });
                let newZones: DeliveryZonePlaceConfig[] = [];
                if (place.deliveryZonePlacePrices)
                    newZones = [...place.deliveryZonePlacePrices].sort(
                        (a, b) => Number(a.price ? a.price : '0') - Number(b.price ? b.price : '0'),
                    );
                setDeliveryZonePlaceConfig(newZones);

                if (initialDeliveryPriceConfigsRef.current.length === 0) {
                    initialDeliveryPriceConfigsRef.current = [...newZones];
                }
            }
        }
    }, [place, setInitialFormData]);

    const validatePrices = useCallback(
        (configs: DeliveryZonePlaceConfig[]): Record<string, ValidationErrorType> => {
            return validateConfigFields<DeliveryZonePlaceConfig>(configs, 'id', ['price'], {
                price: {
                    required: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_REQUIRED'),
                    },
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_NUMBER'),
                    },
                    isZeroOrHiger: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_POSITIVE'),
                    },
                },
            });
        },
        [t],
    );

    const handleDeliveryPriceChange = useCallback((id: string, newPrice: string) => {
        setDeliveryZonePlaceConfig((prev) => {
            const newZones = prev.map((zone) => {
                if (zone.id === id) {
                    return { ...zone, price: newPrice };
                }
                return zone;
            });

            return newZones;
        });
    }, []);

    const hasDeliveryPriceConfigsChanged = useMemo(() => {
        return !initialDeliveryPriceConfigsRef.current.every((initialConfig, index) => {
            const currentConfig = deliveryZonePlaceConfig[index];

            return initialConfig.price === currentConfig.price;
        });
    }, [deliveryZonePlaceConfig]);
    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'place.PLACE_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(
        (documentId?: string) => {
            const data = { ...changedValues };
            if (documentId) data.documentId = documentId;
            delete data.logo;
            if (data.minimumOrderAmount) data.minimumOrderAmount = Number(data.minimumOrderAmount);
            if (data.longitude) data.longitude = Number(data.longitude);
            if (data.latitude) data.latitude = Number(data.latitude);
            data.deliveryZonePlacePrices = deliveryZonePlaceConfig.map((zone) => {
                return { id: zone.id, price: Number(zone.price) };
            });
            return trimValues(data);
        },
        [changedValues, deliveryZonePlaceConfig],
    );

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                let documentId: string | undefined;
                const prevDocument = place?.documentId;

                try {
                    if (changedValues?.logo) {
                        documentId = (await addDocument(dataURLtoFile(changedValues.logo)).unwrap()).documentId;
                    }

                    const validatedPriceErrors = validatePrices(deliveryZonePlaceConfig);
                    setPriceErrors(validatedPriceErrors);

                    if (!isEmpty(validatedPriceErrors)) return;

                    const payload = getPayload(documentId);

                    await editPlace({ id: id as string, payload }).unwrap();

                    if (changedValues?.logo && prevDocument) {
                        await deleteDocument(prevDocument).unwrap();
                    }
                    handleSuccess();

                    navigate(-1);
                } catch (error) {
                    if (documentId) await deleteDocument(documentId).unwrap();
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            deliveryZonePlaceConfig,
            validatePrices,
            addDocument,
            changedValues,
            place?.documentId,
            deleteDocument,
            editPlace,
            getPayload,
            handleErrors,
            handleSubmit,
            handleSuccess,
            id,
            navigate,
        ],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as Place,
        terminals,
        cities,
        onChange: handleChange,
        onSubmit,
        onCancel,
        dataChanged: isDirty || hasDeliveryPriceConfigsChanged,
        isLoading: formData === undefined || isGetTerminalsLoading || isGetCitiesLoading,
        isEditPlaceLoading: isEditPlaceLoading || isAddDocumentLoading || isDeleteDocumentLoading,
        errors: allErrors,
        isAdmin,
        deliveryZonePlaceConfig,
        onDeliveryPriceChange: handleDeliveryPriceChange,
        website: { id: websiteId, name: websiteName },
    };
};

export default useController;
